/** Presentation only. Request eligibility and intake versions remain server owned. */
export function isSignerEmbedLocation(location: Pick<Location, 'pathname' | 'search'>): boolean {
  const values = new URLSearchParams(location.search).getAll('embed');
  return location.pathname === '/forms/agreement' && values.length === 1 && values[0] === '1';
}

export function initializeSignerEmbed(win: Window = window): () => void {
  if (!isSignerEmbedLocation(win.location)) return () => {};
  const body = win.document.body;
  body.dataset.signerEmbed = 'true';
  if (win.parent === win) return () => { delete body.dataset.signerEmbed; };

  let pending: number | undefined;
  let previousHeight: number | undefined;
  const measure = () => {
    pending = undefined;
    // The embedded body has intrinsic height, with no viewport minimum. Unlike
    // documentElement.scrollHeight this can shrink after a tall stage closes.
    const measured = body.getBoundingClientRect().height;
    if (!Number.isFinite(measured)) return;
    const height = Math.max(300, Math.min(24000, Math.ceil(measured)));
    if (height === previousHeight) return;
    previousHeight = height;
    win.parent.postMessage({ type: 'ohf:signer:height', height }, win.location.origin);
  };
  const schedule = () => {
    if (pending === undefined) pending = win.requestAnimationFrame(measure);
  };
  const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule);
  observer?.observe(body);
  win.addEventListener('load', schedule);
  win.addEventListener('resize', schedule);
  schedule();
  return () => {
    observer?.disconnect();
    win.removeEventListener('load', schedule);
    win.removeEventListener('resize', schedule);
    if (pending !== undefined) win.cancelAnimationFrame(pending);
    delete body.dataset.signerEmbed;
  };
}
